// redux/leads-queue/slice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_COMPANY } from "./constants";
import { fetchCompanies, fetchLeadsForms } from "./thunks";
import { getDefaultForm } from "../../../common/formSelection";
import type { ActiveCompany, Company, LeadForm } from "./types";

type SharedLeadsState = {
  companies: Company[];
  activeCompany: ActiveCompany;
  leadForms: LeadForm[];
  formId: number | null;

  loadingCompanies: boolean;
  loadingForms: boolean;
  error: any;
};

const initialState: SharedLeadsState = {
  companies: [],
  activeCompany: DEFAULT_COMPANY,
  leadForms: [],
  formId: null,

  loadingCompanies: false,
  loadingForms: false,
  error: null,
};

const leadsQueueSlice = createSlice({
  name: "leadsQueue",
  initialState,
  reducers: {
    setActiveCompany: (state, action: PayloadAction<ActiveCompany>) => {
      state.activeCompany = action.payload;
      state.leadForms = [];
      state.formId = null;
    },
    setQueueFormId: (state, action: PayloadAction<number | null>) => {
      state.formId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Companies
      .addCase(fetchCompanies.pending, (state) => {
        state.loadingCompanies = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loadingCompanies = false;
        state.companies = action.payload;

        if (!state.activeCompany?.company_id && action.payload.length > 0) {
          const first = action.payload[0];
          state.activeCompany = {
            company_id: first.id,
            company_name: first.name,
            company_type: first.type,
          };
        }
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loadingCompanies = false;
        state.error = action.payload;
      })

      // Lead forms
      .addCase(fetchLeadsForms.pending, (state) => {
        state.loadingForms = true;
        state.error = null;
      })
      .addCase(fetchLeadsForms.fulfilled, (state, action) => {
        state.loadingForms = false;
        state.leadForms = action.payload || [];
        if (!state.formId && state.leadForms.length > 0) {
          const defaultForm = getDefaultForm(state.leadForms);
          state.formId = defaultForm ? defaultForm.id : state.leadForms[0].id;
        }
      })
      .addCase(fetchLeadsForms.rejected, (state, action) => {
        state.loadingForms = false;
        state.error = action.payload;
      });
  },
});

export const { setActiveCompany, setQueueFormId } = leadsQueueSlice.actions;
export default leadsQueueSlice.reducer;
